import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export const Pricing = () => {
  const [isYearly, setIsYearly] = useState(false);

  const plans = [
  {
    name: "Free",
    monthlyPrice: 0,
    yearlyPrice: 0,
    period: "/month",
    description: "Start your learning path.",
    features: [
    "3 uploads, pastes, and records / day",
    "5 AI chats / day (10 / month with Study+ mode)",
    "10 quiz answers / day",
    "2 practice exams / month",
    "Upload files, each up to 100 pages / 10 MB in size"],

    popular: false
  },
  {
    name: "Pro",
    monthlyPrice: 75,
    yearlyPrice: 45,
    period: "/month",
    description: "Learn at a higher level.",
    features: [
    "Unlimited uploads, pastes, and records",
    "Unlimited AI chats (100 / day with Study+ mode)",
    "Unlimited quiz generation",
    "Unlimited practice exams",
    "Upload files, each up to 2000 pages / 300 MB in size"],

    popular: true
  },
  {
    name: "Team",
    monthlyPrice: 56,
    yearlyPrice: 34,
    period: "/month/seat",
    description: "For study groups and teams.",
    features: [
    "Everything in Pro +",
    "Discounts scale with team size",
    "Shared spaces",
    "Centralized team billing",
    "Add, remove and set custom permissions for team members"],

    popular: false
  }];


  return (
    <section id="pricing" className="container mx-auto px-4 py-16 space-y-12">
      {/* Header */}
      <div className="flex flex-col items-center gap-6 text-center">
        {/* Title */}
        <h2 className="text-4xl md:text-5xl text-foreground font-medium lg:text-6xl">Less Than One Tutoring Session. Better Than Any Tutor.</h2>

        {/* Description */}
        <p className="text-lg text-muted-foreground max-w-2xl">
          Unlimited materials. Unlimited questions. Unlimited practice. One flat price — cancel any time.
        </p>

        {/* Billing Toggle */}
        <div className="text-sm font-medium">
          <div className="bg-muted items-center justify-center inline-flex h-auto rounded-full p-1">
            <button
              onClick={() => setIsYearly(false)}
              className={`items-center cursor-pointer justify-center py-2 px-3 text-center inline-flex w-full h-auto min-h-[2.50rem] rounded-full overflow-visible transition-colors ${
              !isYearly ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`
              }>

              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`items-center cursor-pointer justify-between py-2 px-3 text-center flex w-full h-auto min-h-[2.50rem] rounded-full overflow-visible transition-colors ${
              isYearly ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`
              }>

              Yearly
              <span className="text-xs rounded-full px-2 py-0.5 ml-2 whitespace-nowrap" style={{ color: 'hsl(142 71% 30%)', backgroundColor: 'hsl(142 71% 45% / 0.15)' }}>
                Save 40%
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {plans.map((plan, index) =>
        <Card
          key={index}
          className={`relative flex flex-col ${
          plan.popular ? "border-primary shadow-lg scale-105 bg-card" : "bg-card"}`
          }>

            <CardHeader>
              <div
              className={`rounded-xl p-6 mb-4 ${plan.popular ? "bg-primary text-primary-foreground" : "bg-muted"}`}>

                <div className="flex items-center justify-between mb-6">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  {plan.popular &&
                <span className="text-primary-foreground text-xs rounded-full px-2 py-0.5 bg-primary-foreground/10 border border-primary-foreground/20 whitespace-nowrap flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      Popular
                    </span>
                }
                </div>
                <div className="text-4xl font-bold">
                  SAR {isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                  {plan.period && <span className="text-lg font-normal">{plan.period}</span>}
                </div>
                {isYearly && plan.monthlyPrice > 0 &&
              <p className="text-sm mt-2 opacity-80">Billed SAR {plan.yearlyPrice * 12}/year</p>
              }
              </div>
              <CardDescription className="text-base">{plan.description}</CardDescription>
            </CardHeader>

            <CardContent className="flex-grow">
              {plan.name === "Free" ?
            <Button className="w-full mb-6 group" variant={plan.popular ? "default" : "outline"} asChild>
                  <Link to="/auth/signup">
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button> :
            plan.name === "Team" ?
            <Button className="w-full mb-6 group" variant={plan.popular ? "default" : "outline"} asChild>
                  <Link to="/contact">
                    Contact Us
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button> :

            <Button className="w-full mb-6 group" variant={plan.popular ? "default" : "outline"}>
                  Start your 7 days free trial
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
            }

              <div className="space-y-4">
                <h4 className="font-semibold text-lg">Features:</h4>
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) =>
                <li key={idx} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm font-medium">{feature}</span>
                    </li>
                )}
                </ul>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </section>);

};